/**
 * The phone dispatch view's rules (#2487). Pure: no React, no i18n instance.
 *
 * The headline and the reminder card are composed ONLY from S3's focus cards
 * (`board/focusCards.ts`), so the phone and the desktop never say two
 * different things about "now". The six-step bar reads today's board row and
 * `phaseOf` (the current step). Print is never done: the system records no print event.
 */
import { detailStringArray, parseDetails } from '@features/admin';
import type { ReviewQueueItem } from '@/services/review-queue';
import {
  POST_CUTOFF_CHANGE_TYPE,
  REVIEW_QUEUE_KIND,
  type PostCutoffChangeType,
} from '@/services/review-queue.constants';
import type { BoardDay } from '../board/boardModel';
import type { FocusCard } from '../board/focusCards';
import type { Phase, RowPhase } from '../board/rowCopy';
import type { EnRouteRow } from '../panes/enRouteModel';

export const PHONE_STEPS = ['intake', 'dispatch', 'assign', 'print', 'deliver', 'close'] as const;
export type PhoneStep = (typeof PHONE_STEPS)[number];
export type StepState = 'done' | 'on' | 'todo';

// Literal keys passed to `t()` by lookup; `__tests__/phoneLabels.test.ts` pins them.
export const STEP_LABEL = {
  intake: 'dispatch.phone.step.intake',
  dispatch: 'dispatch.phone.step.dispatch',
  assign: 'dispatch.phone.step.assign',
  print: 'dispatch.phone.step.print',
  deliver: 'dispatch.phone.step.deliver',
  close: 'dispatch.phone.step.close',
} as const satisfies Record<PhoneStep, string>;

export const CHANGE_LABEL = {
  address: 'dispatch.phone.inbox.change.address',
  quantity: 'dispatch.phone.inbox.change.quantity',
  cancel: 'dispatch.phone.inbox.change.cancel',
  other: 'dispatch.phone.inbox.change.other',
} as const satisfies Record<PostCutoffChangeType, string>;

const ALL_TODO: StepState[] = ['todo', 'todo', 'todo', 'todo', 'todo', 'todo'];

/** Step states by phase, in `PHONE_STEPS` order. Print (index 3) is never done. */
const STEPS_BY_PHASE = {
  dispatch: ['done', 'on', 'todo', 'todo', 'todo', 'todo'],
  assign: ['done', 'done', 'on', 'todo', 'todo', 'todo'],
  depart: ['done', 'done', 'done', 'on', 'todo', 'todo'],
  deliver: ['done', 'done', 'done', 'todo', 'on', 'todo'],
  closed: ['done', 'done', 'done', 'todo', 'done', 'done'],
} as const satisfies Record<RowPhase, readonly StepState[]>;

/** The six-step bar for today's row (#2487 B3/B4). */
export function phoneSteps(
  row: BoardDay | undefined,
  phase: Phase | null,
  /** False when packaging does not end before dispatch: the packaging gate says nothing (#2631). */
  windowed: boolean,
): StepState[] {
  if (!row || row.total === 0 || phase === null || phase === 'none') return [...ALL_TODO];
  if (phase === 'dispatch' && windowed && row.nextGate?.kind === 'packaging_cutoff') {
    return ['on', 'todo', 'todo', 'todo', 'todo', 'todo'];
  }
  return [...STEPS_BY_PHASE[phase]];
}

/** A translation key plus its params; a headline is several, joined as-is. */
export interface PhoneText {
  key: string;
  params: Record<string, string | number>;
}

export interface PhoneHeadline {
  parts: PhoneText[];
  /** A read the cards need failed: say so, never "all clear". */
  failed: boolean;
}

/**
 * The verdict headline (#2487 N1): the progress title (plus how many drivers
 * run past the window end), else the first card's title. With no card it says
 * "all clear" only when S3 does, says the read failed when one did, and is
 * null while the cards are not in.
 */
export function phoneHeadline(
  cards: readonly FocusCard[],
  late: { count: number; windowEnd: string } | null,
  status: { allClear: boolean; failed: boolean },
): PhoneHeadline | null {
  const progress = cards.find((card) => card.kind === 'progress');
  if (progress) {
    const parts: PhoneText[] = [progress.title];
    if (late && late.count > 0) {
      parts.push({
        key: 'dispatch.phone.headline.lateSuffix',
        params: { count: late.count, time: late.windowEnd },
      });
    }
    return { parts, failed: false };
  }
  const first = cards[0];
  if (first) return { parts: [first.title], failed: false };
  if (status.failed) return { parts: [{ key: 'error.loadFailed', params: {} }], failed: true };
  if (status.allClear) {
    return { parts: [{ key: 'dispatch.board.focus.allClear', params: {} }], failed: false };
  }
  return null;
}

/** The reminder card (#2487 N2): the first card that asks for attention or blocks. */
export function pickHero(cards: readonly FocusCard[]): FocusCard | null {
  return cards.find((card) => card.tone === 'attn' || card.tone === 'block') ?? null;
}

/**
 * Drivers latest ETA first, those without an ETA last in their incoming order
 * (#2487 N6). A copy: colours stay as `buildEnRouteRows` gave them.
 */
export function sortPhoneRows(rows: readonly EnRouteRow[]): EnRouteRow[] {
  return [...rows].sort((a, b) => {
    if (a.etaMs === null || b.etaMs === null) {
      return (a.etaMs === null ? 1 : 0) - (b.etaMs === null ? 1 : 0);
    }
    return b.etaMs - a.etaMs;
  });
}

const CHANGE_TYPES: readonly string[] = Object.values(POST_CUTOFF_CHANGE_TYPE);
const isChangeType = (value: string): value is PostCutoffChangeType => CHANGE_TYPES.includes(value);

/** What changed on a post-cutoff row, as label keys; empty for any other row (#2487 I4). */
export function inboxChangeText(item: ReviewQueueItem): string[] {
  if (item.kind !== REVIEW_QUEUE_KIND.POST_CUTOFF_ORDER_CHANGE) return [];
  const details = parseDetails(item.details_json);
  if (details === null) return [];
  return detailStringArray(details, 'change_types')
    .filter(isChangeType)
    .map((type) => CHANGE_LABEL[type]);
}
